import {
  MutationStatus,
  MutatorState,
  useAppStorage,
  useMutation,
  useObservable,
} from '@aesop-fables/scrinium';
import OrgPage from '../organization/OrgPage';
import { AdminTierTypeEnum, DrillDownWizard, drillDownWizardKey } from '../../services/drillDown';
import { useService } from '@aesop-fables/containr-react';
import GroupPage from '../group/GroupPage';
import TeamPage from '../team/TeamPage';
import { SelectOrganization, UnselectOrganization } from '../../data/organization/mutations';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useLoading } from '../../hooks/useLoading';
import { useRestorePreNavSelectedTiers } from '../../hooks/useRestorePreNavSelectedTiers';
import { SelectGroup, UnselectGroup } from '../../data/group/mutations';
import { SelectTeam, UnselectTeam, SaveTeamData } from '../../data/team/mutations';
import { ToastbarContext } from '../../App';
import { SignUpCompartments, signUpStorageKey } from '../../data/signUp';
import { Toast } from '../../models/Toast';
import { SignUpWizard, signUpWizardKey } from '../../services/signUp';
import { OrgSignUpStateData } from '../../models/SignUpData';
import { GroupCompartments, groupStorageKey } from '../../data/group';
import { TeamCompartments, teamStorageKey } from '../../data/team';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';
import { GroupData } from '../../models/GroupData';
import { TeamData } from '../../models/TeamData';
import { OrganizationData } from '../../models/OrganizationData';
import { updateCrumbs } from '../../components/DrillDownBreadcrumbs';

export type OrgStatus = 'complete' | 'incomplete';

const AdminDashboard = () => {
  const appStorage = useAppStorage();
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoading } = useLoading();
  const { setToast } = useContext(ToastbarContext);
  const [orgStatus, setOrgStatus] = useState<OrgStatus | undefined>(undefined);
  const [isNavigatingToOrg, setIsNavigatingToOrg] = useState(false);

  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const signUpWizard = useService<SignUpWizard>(signUpWizardKey);
  const orgSignUpState = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData>('orgSignUpState'),
  );
  const tier = useObservable(drillDownWizard.tier$);

  const selectGroup = useMutation(new SelectGroup());
  const selectTeam = useMutation(new SelectTeam());
  const selectOrganization = useMutation(new SelectOrganization());

  const unselectGroup = useMutation(new UnselectGroup());
  const unselectTeam = useMutation(new UnselectTeam());
  const unselectOrganization = useMutation(new UnselectOrganization());

  const saveTeamData = useMutation(new SaveTeamData());

  const groupDataCache = appStorage.retrieve<GroupCompartments>(groupStorageKey);
  const teamDataCache = appStorage.retrieve<TeamCompartments>(teamStorageKey);
  const orgDataCache = appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
  const groupData = useObservable(groupDataCache.observe$<GroupData>('group'));
  const teamData = useObservable(teamDataCache.observe$<TeamData>('team'));
  const orgData = useObservable(orgDataCache.observe$<OrganizationData>('organization'));
  const [isBack, setIsBack] = useState(false);

  useRestorePreNavSelectedTiers({
    selectGroup,
    selectTeam,
    unselectOrganization,
    unselectTeam,
    unselectGroup,
    setIsNavigatingToOrg,
  });

  useEffect(() => {
    window.onpopstate = () => {
      setIsBack(true);
    };

    const handleClick = () => {
      setIsBack(false);
    };

    window.addEventListener('click', handleClick);
    return () => {
      window.removeEventListener('click', handleClick);
    };
  });

  useEffect(() => {
    if (
      isBack &&
      (location.pathname.includes('/choose-group') || tier === AdminTierTypeEnum.Group)
    ) {
      updateCrumbs({ groupData, unselectGroup });
    } else if (
      isBack &&
      (location.pathname.includes('/teams') || tier === AdminTierTypeEnum.Team)
    ) {
      updateCrumbs({ teamData, unselectTeam });
    } else if (
      isBack &&
      (location.pathname.includes('/organizations') || tier === AdminTierTypeEnum.Organization)
    ) {
      updateCrumbs({ orgData, unselectOrganization });
    }
  }, [
    groupData,
    isBack,
    location.pathname,
    orgData,
    teamData,
    tier,
    unselectGroup,
    unselectOrganization,
    unselectTeam,
  ]);

  useEffect(() => {
    if (selectGroup.status === MutationStatus.Complete && !isNavigatingToOrg) {
      navigate('/controller/teams');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectGroup.status]);

  useEffect(() => {
    if (selectTeam.status === MutationStatus.Complete && !isNavigatingToOrg) {
      navigate('/controller/organizations');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectTeam.status]);

  useEffect(() => {
    if (!orgStatus) {
      return;
    }
    if (orgStatus === 'complete') {
      // scrinium marks a mutation Complete even when it throws, so error must be checked first
      if (selectOrganization.error) {
        setToast(
          new Toast({
            message: 'Something went wrong loading this organization',
            severity: 'error',
            open: true,
          }),
        );
        const tierRoute =
          tier === AdminTierTypeEnum.Organization
            ? '/controller/organizations'
            : tier === AdminTierTypeEnum.Team
              ? '/controller/teams'
              : undefined;
        if (tierRoute && location.pathname !== tierRoute) {
          navigate(tierRoute);
        }
        setLoading(false);
        setOrgStatus(undefined);
        setIsNavigatingToOrg(false);
        return;
      }
      if (selectOrganization.status === MutationStatus.Complete) {
        if (orgSignUpState?.onboardingComplete) {
          navigate('/');
          setLoading(false);
          setOrgStatus(undefined);
          setIsNavigatingToOrg(false);
        } else {
          (async () => {
            await signUpWizard.init();
            const step = signUpWizard.resumeOnboarding(orgSignUpState?.lastCompletedStep ?? '');
            navigate(step?.route ?? '');
            setLoading(false);
            setOrgStatus(undefined);
            setIsNavigatingToOrg(false);
          })();
        }
        return;
      }
    }
    if (orgStatus === 'incomplete') {
      if (selectOrganization.error) {
        setToast(
          new Toast({
            message:
              'Could not continue. If this error persists please remove this organization and start over.',
            severity: 'error',
            open: true,
          }),
        );
        setLoading(false);
        setOrgStatus(undefined);
      } else if (selectOrganization.status === MutationStatus.Complete) {
        (async () => {
          await signUpWizard.init();
          const step = signUpWizard.resumeOnboarding(orgSignUpState?.lastCompletedStep ?? '');
          navigate(step?.route ?? '');
          setLoading(false);
          setOrgStatus(undefined);
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectOrganization.status, signUpDataCache]);

  useEffect(() => {
    if (saveTeamData?.error?.message.includes('400')) {
      setToast(
        new Toast({
          message: 'Team name is already taken. Please use a unique name.',
          severity: 'error',
          open: true,
          autoHideDuration: 3000,
        }),
      );
      setLoading(false);
    } else if (saveTeamData?.status === MutationStatus.Complete) {
      setToast(
        new Toast({
          message: 'Team created!',
          severity: 'success',
          open: true,
          autoHideDuration: 3000,
        }),
      );
      navigate('/controller/organizations');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTeamData.status, setToast]);

  return (
    <>
      {!isNavigatingToOrg && tier === AdminTierTypeEnum.Organization && (
        <OrgPage select={selectOrganization} setOrgStatus={setOrgStatus} />
      )}
      {!isNavigatingToOrg && tier === AdminTierTypeEnum.Team && (
        <TeamPage
          select={selectTeam}
          saveData={saveTeamData}
          selectGroup={selectGroup}
          selectOrganization={selectOrganization}
          setOrgStatus={setOrgStatus}
          setIsNavigatingToOrg={setIsNavigatingToOrg}
        />
      )}
      {!isNavigatingToOrg && tier === AdminTierTypeEnum.Group && (
        <GroupPage
          select={selectGroup}
          selectTeam={selectTeam}
          selectOrganization={selectOrganization}
          setOrgStatus={setOrgStatus}
          setIsNavigatingToOrg={setIsNavigatingToOrg}
        />
      )}
    </>
  );
};

export interface AdminPageProps {
  select: MutatorState<number>;
  selectTeam?: MutatorState<number>;
  selectGroup?: MutatorState<number>;
  saveData?: MutatorState<any>;
  selectOrganization?: MutatorState<number>;
  setOrgStatus?: React.Dispatch<React.SetStateAction<OrgStatus | undefined>>;
  setIsNavigatingToOrg?: React.Dispatch<React.SetStateAction<boolean>>;
}

export default AdminDashboard;
